<template>
    <form method="GET" action="." autocomplete="off" @submit="onSubmit" class="form">
        <label for="adres" class="label">Uw adres</label>
        <div class="searchbar">
            <div class="autocomplete-container">
                <input
                    class="input"
                    type="search"
                    id="adres"
                    name="adres"
                    autocomplete="off"
                    placeholder="Vul uw adres in"
                    role="combobox"
                    @keydown="onKeyDown"
                    @input="onInput"
                    @blur="onInputBlur"
                    aria-haspopup="listbox"
                    aria-owns="autocomplete-options"
                    :aria-describedby="!showResults ? 'autocomplete-assistive-hint' : null"
                    :aria-expanded="results.length > 0"
                    :aria-activedescendant="
                        selectedIndex !== -1 ? `autocomplete-option-${selectedIndex}` : null
                    "
                    v-model="query"
                />
                <div class="autocomplete" v-if="showResults">
                    <ul class="list" role="listbox" aria-labelledby="adres">
                        <li
                            v-for="(result, i) in results"
                            :id="`autocomplete-option-${i}`"
                            role="option"
                            :aria-posinset="i + 1"
                            :aria-setsize="results.length"
                            :aria-selected="selectedIndex === i"
                            :class="{ hover: selectedIndex === i }"
                            :key="result.id"
                            @mousedown.prevent
                            @click.prevent="(e) => onSelect(e, results[i])"
                        >
                            {{ result.weergavenaam }}
                        </li>
                        <li v-if="results.length === 0" aria-selected="false">
                            Er zijn geen adressen gevonden
                        </li>
                    </ul>
                </div>
                <span id="autocomplete-assistive-hint" style="display: none">
                    Gebruik de pijltjes omhoog en omlaag om te navigeren door de lijst als er
                    adressen zijn.
                </span>
            </div>
            <button type="submit" class="button utrecht-button">Voorrangsscholen weergeven</button>
        </div>
    </form>
</template>

<script>
import { getPrioritySchools } from '../services'

const suggestEndpoint = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest'
const freeEndpoint = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/free'

const keyCodes = {
    13: 'enter',
    27: 'escape',
    32: 'space',
    38: 'up',
    40: 'down',
}

const isPrintableKeyCode = (keyCode) => {
    return (
        (keyCode > 47 && keyCode < 58) || // number keys
        keyCode === 32 ||
        keyCode === 8 || // spacebar or backspace
        (keyCode > 64 && keyCode < 91) || // letter keys
        (keyCode > 95 && keyCode < 112) || // numpad keys
        (keyCode > 185 && keyCode < 193) || // ;=,-./` (in order)
        (keyCode > 218 && keyCode < 223) // [\]' (in order)
    )
}

export default {
    name: 'SearchForm',
    data() {
        return {
            query: '',
            selectedIndex: -1,
            results: [],
            showResults: false,
        }
    },
    methods: {
        highlightNext() {
            if (this.selectedIndex < this.results.length - 1) {
                this.selectedIndex++
            }
        },
        highlightPrevious() {
            if (this.selectedIndex > 0) {
                this.selectedIndex--
            }
        },
        onInput(e) {
            if (!this.query) {
                return
            }

            this.onSearch()
        },
        onKeyDown(e) {
            switch (keyCodes[e.keyCode]) {
                case 'up':
                    this.highlightPrevious()
                    break
                case 'down':
                    this.highlightNext()
                    break
                case 'enter':
                    e.preventDefault()
                    this.onSelect(e, this.results[this.selectedIndex])
                    break
            }
        },
        onInputBlur(e) {
            this.showResults = false
        },
        async onSearch() {
            this.selectedIndex = -1

            try {
                const result = await fetch(
                    `${suggestEndpoint}?fq=gemeentenaam:utrecht&fq=type:adres&q=${encodeURIComponent(
                        this.query
                    )}`
                )
                const data = await result.json()

                this.results = data.response.docs
                this.showResults = true
            } catch (e) {
                console.error(e)
            }
        },
        async onSelect(e, object) {
            this.query = object.weergavenaam
            this.selected = object
            this.selectedIndex = -1
            this.results = []
            this.showResults = false
        },
        async onSubmit(e) {
            e.preventDefault()

            if (!this.selected) {
                return
            }

            try {
                const result = await fetch(
                    `${freeEndpoint}?q=${encodeURIComponent('id:' + this.selected.id)}`
                )

                const data = await result.json()
                if (!data.response.docs) {
                    return
                }

                const object = data.response.docs[0]

                const centeroide = /POINT\(([\d.]+) ([\d.]+)\)/.exec(object.centroide_rd)
                const parsedCenteroide = [parseFloat(centeroide[1]), parseFloat(centeroide[2])]

                try {
                    const scholen = await getPrioritySchools(object.nummeraanduiding_id)
                    this.$emit('highlight-schools', scholen, parsedCenteroide)
                } catch (e) {
                    console.log('Could not highlight schools', e)
                }

                this.$emit('set-marker', parsedCenteroide)

                this.query = object.weergavenaam
                this.results = []
            } catch (e) {
                console.error(e)
            }

            window.top.postMessage('atlas:event:search', '*')
        },
    },
    watch: {
        query(value) {
            if (value === '') {
                this.selected = null
                this.results = []
                this.$emit('clear-query')
            }
        },
    },
}
</script>

<style scoped>
@media (min-width: 576px) {
    .form {
        max-width: 600px;
    }
}

.autocomplete-container {
    position: relative;
    max-width: 500px;
    flex-grow: 1;
}

.label {
    display: block;
}

.searchbar {
    display: block;
}

@media (min-width: 576px) {
    .searchbar {
        display: flex;
    }
}

.input {
    width: 100%;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 12px;
    padding-right: 12px;
    font-size: var(--font-size-normal);
    margin-top: 5px;
    border: 1px solid var(--color-grey-40);
}

.button-container {
    position: relative;
}

.button {
    margin-top: 5px;
}

@media (min-width: 576px) {
    .button {
        margin-top: 0;
        margin-left: 5px;
    }
}

.autocomplete {
    position: absolute;
    width: 100%;
    z-index: 2;
    background-color: #fff;
    border: 1px solid var(--color-grey-40);
    border-top: 0;
    padding: 12px 0;
}

.list li {
    display: block;
    color: var(--color-primary);
    padding: 3px 16px;
    text-decoration: none;
}

.list li:hover,
.list li:active,
.list .hover {
    text-decoration: underline;
}
</style>
